import React, { useState, useEffect } from 'react';
import './MenuDesign.css';

const MenuDesign = () => {
  const [activeCategory, setActiveCategory] = useState('starters');
  const [menuData, setMenuData] = useState({});
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [lunchtimeStatus, setLunchtimeStatus] = useState('waiting'); // 'active', 'waiting', 'ended'
  const [nextLunchtime, setNextLunchtime] = useState('');
  const [lunchSpecials, setLunchSpecials] = useState([]);

  useEffect(() => {
    // Load menu data, categories, and lunch specials
    Promise.all([
      fetch('/menu/menu.json'),
      fetch('/menu/categories.json'),
      fetch('/menu/lunch-specials.json')
    ])
    .then(responses => {
      return Promise.all(responses.map(response => {
        if (!response.ok) {
          throw new Error('Failed to load data');
        }
        return response.json();
      }));
    })
    .then(([menuData, categoriesData, lunchSpecialsData]) => {
      setMenuData(menuData);
      setLunchSpecials(lunchSpecialsData);
      
      // Convert categories data to the format expected by the component
      const categoriesArray = Object.entries(categoriesData)
        .sort(([,a], [,b]) => a.order - b.order) // Sort by order
        .map(([key, category]) => ({
          key: key,
          label: category.name,
          italian: category.italian,
          information: category.information || ''
        }));
      
      setCategories(categoriesArray);
      
      // Set the first category as active if no category is currently active
      if (categoriesArray.length > 0 && !categoriesArray.find(cat => cat.key === activeCategory)) {
        setActiveCategory(categoriesArray[0].key);
      }
      
      setLoading(false);
    })
    .catch(err => {
      console.error('Error loading data:', err);
      setError(err.message);
      setLoading(false);
    });
  }, []);

  // Function to calculate lunchtime countdown
  const calculateLunchtimeCountdown = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTime = currentHour * 60 + currentMinute;
    
    // Lunchtime is 11:30 AM (690 minutes) to 3:00 PM (900 minutes)
    const lunchStart = 690; // 11:30 AM
    const lunchEnd = 900;   // 3:00 PM
    
    if (currentTime >= lunchStart && currentTime < lunchEnd) {
      // Lunchtime is active - countdown to end
      const timeLeft = lunchEnd - currentTime;
      const hours = Math.floor(timeLeft / 60);
      const minutes = timeLeft % 60;
      const seconds = 0; // We'll update this with useEffect
      
      setLunchtimeStatus('active');
      setCountdown({ hours, minutes, seconds });
      setNextLunchtime('');
    } else if (currentTime < lunchStart) {
      // Before lunchtime - countdown to start
      const timeLeft = lunchStart - currentTime;
      const hours = Math.floor(timeLeft / 60);
      const minutes = timeLeft % 60;
      const seconds = 0;
      
      setLunchtimeStatus('waiting');
      setCountdown({ hours, minutes, seconds });
      
      // Calculate next lunchtime
      const nextLunchDate = new Date(now);
      nextLunchDate.setHours(11, 30, 0, 0);
      setNextLunchtime(nextLunchDate.toLocaleDateString('en-GB', { 
        weekday: 'long',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true 
      }));
    } else {
      // After lunchtime - countdown to tomorrow
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(11, 30, 0, 0);
      
      const timeLeft = tomorrow.getTime() - now.getTime();
      const hours = Math.floor(timeLeft / (1000 * 60 * 60));
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
      
      setLunchtimeStatus('ended');
      setCountdown({ hours, minutes, seconds });
      setNextLunchtime(tomorrow.toLocaleDateString('en-GB', { 
        weekday: 'long',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true 
      }));
    }
  };

  // Update countdown every second
  useEffect(() => {
    calculateLunchtimeCountdown();
    const interval = setInterval(() => {
      calculateLunchtimeCountdown();
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  const MenuItem = ({ item }) => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      padding: 'clamp(12px, 4vw, 24px)',
      marginBottom: '12px',
      transition: 'all 0.3s',
      borderLeft: '4px solid #b91c1c',
      minWidth: 0,
      wordBreak: 'break-word',
      maxWidth: '100%'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', gap: '8px' }}>
        <h3 style={{ fontWeight: 'bold', fontSize: 'clamp(1rem, 4vw, 20px)', color: '#2f2f2f', margin: 0, flex: 1 }}>{item.name}</h3>
        <span style={{ color: '#b91c1c', fontWeight: 'bold', fontSize: 'clamp(1rem, 4vw, 20px)', marginLeft: '8px', whiteSpace: 'nowrap' }}>{item.price}</span>
      </div>
      {item.description && (
        <p style={{ color: '#666', fontSize: 'clamp(0.85rem, 3vw, 14px)', fontStyle: 'italic', margin: 0 }}>{item.description}</p>
      )}
    </div>
  );

  const CategoryButton = ({ category, isActive, onClick }) => (
    <button
      onClick={onClick}
      style={{
        padding: '12px 24px',
        borderRadius: '8px',
        fontWeight: '600',
        transition: 'all 0.3s',
        textAlign: 'center',
        backgroundColor: isActive ? '#b91c1c' : 'white',
        color: isActive ? 'white' : '#2f2f2f',
        border: 'none',
        cursor: 'pointer',
        boxShadow: isActive ? '0 4px 8px rgba(0,0,0,0.2)' : '0 2px 4px rgba(0,0,0,0.1)',
        transform: isActive ? 'scale(1.05)' : 'scale(1)'
      }}
    >
      <div style={{ fontSize: '18px' }}>{category.label}</div>
      <div style={{ fontSize: '14px', opacity: 0.8, fontStyle: 'italic' }}>{category.italian}</div>
    </button>
  );

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        padding: '32px 8px',
        fontFamily: "'Segoe UI', sans-serif",
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: '24px', color: '#2f2f2f', marginBottom: '16px' }}>Loading Menu...</h2>
          <div style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #b91c1c', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }}></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        padding: '32px 8px',
        fontFamily: "'Segoe UI', sans-serif",
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: '24px', color: '#b91c1c', marginBottom: '16px' }}>Error Loading Menu</h2>
          <p style={{ color: '#666', marginBottom: '16px' }}>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            style={{
              padding: '12px 24px',
              backgroundColor: '#b91c1c',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      padding: '0 8px 32px',
      fontFamily: "'Segoe UI', sans-serif",
      boxSizing: 'border-box',
      maxWidth: '100vw',
      overflowX: 'hidden'
    }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '48px', paddingTop: '64px' }}>
          <h1 style={{ fontSize: 'clamp(2.2rem, 7vw, 60px)', fontWeight: 'bold', color: '#2f2f2f', marginBottom: '16px' }}>Menu</h1>
          <div style={{ width: '96px', height: '4px', backgroundColor: '#b91c1c', margin: '0 auto 24px' }}></div>
          <p style={{ fontSize: 'clamp(1rem, 4vw, 20px)', color: '#4a7043', fontStyle: 'italic' }}>La Nostra Cucina Italiana</p>
        </div>

        {/* Category Navigation */}
        <nav aria-label="Menu categories" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px', marginBottom: '32px', maxWidth: '100vw' }}>
          {categories.map((category) => (
            <CategoryButton
              key={category.key}
              category={category}
              isActive={activeCategory === category.key}
              onClick={() => setActiveCategory(category.key)}
            />
          ))}
        </nav>

        {/* Menu Items */}
        <section aria-labelledby="menu-category-heading" style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '32px' }}>
            <div style={{ height: '1px', backgroundColor: '#4a7043', flex: 1 }}></div>
            <div style={{ padding: '0 12px' }}>
              <h2 id="menu-category-heading" style={{ fontSize: 'clamp(1.3rem, 5vw, 36px)', fontWeight: 'bold', color: '#2f2f2f', textAlign: 'center', margin: 0 }}>
                {categories.find(cat => cat.key === activeCategory)?.label}
              </h2>
              <p style={{ fontSize: 'clamp(1rem, 4vw, 20px)', color: '#4a7043', fontStyle: 'italic', textAlign: 'center', margin: '8px 0 0 0' }}>
                {categories.find(cat => cat.key === activeCategory)?.italian}
              </p>
            </div>
            <div style={{ height: '1px', backgroundColor: '#4a7043', flex: 1 }}></div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', maxWidth: '100vw', overflowX: 'hidden' }}>
            {menuData[activeCategory]?.map((item, index) => (
              <MenuItem key={index} item={item} />
            ))}
          </div>
          
          {/* Dynamic Information Box */}
          {(() => {
            // Get category information from categories data
            const currentCategory = categories.find(cat => cat.key === activeCategory);
            if (currentCategory && currentCategory.information && currentCategory.information.trim() !== '') {
              return (
                <div style={{
                  backgroundColor: '#4a7043',
                  color: 'white',
                  borderRadius: '8px',
                  padding: '24px',
                  textAlign: 'center',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  marginTop: '24px'
                }}>
                  <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '8px' }}>{currentCategory.label} Information</h3>
                  <p style={{ fontSize: '18px', margin: 0 }}>{currentCategory.information}</p>
                </div>
              );
            }
            return null;
          })()}
        </section>

        {/* Special Menu Section */}
        <section className="lunch-special-section" style={{ marginTop: '48px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '32px' }}>
            <div style={{ height: '1px', backgroundColor: '#4a7043', flex: 1 }}></div>
            <div style={{ padding: '0 12px' }}>
              <h2 style={{ fontSize: 'clamp(1.3rem, 5vw, 36px)', fontWeight: 'bold', color: '#2f2f2f', textAlign: 'center', margin: 0 }}>
                Special Menu
              </h2>
            </div>
            <div style={{ height: '1px', backgroundColor: '#4a7043', flex: 1 }}></div>
          </div>

          {/* Lunch Special Card */}
          <div style={{
            maxWidth: '600px',
            margin: '0 auto',
            position: 'relative',
            marginTop: '100px'
          }}>
            {/* Pep's Image - Peeking over the top */}
            <div style={{
              position: 'absolute',
              top: '-160px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10,
              width: '345px',
              height: '345px'
            }}>
              <img 
                src="/Pep_Lunch_Special.png" 
                alt="Pep's Lunch Special" 
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
              />
            </div>

            {/* Green Menu Card */}
            <div style={{
              backgroundColor: '#4a7043',
              color: 'white',
              borderRadius: '20px',
              padding: '120px 30px 30px 30px',
              textAlign: 'center',
              boxShadow: '0 8px 16px rgba(0,0,0,0.2)'
            }}>
              {/* Menu Items */}
              <div style={{ marginBottom: '20px', marginTop: '40px' }}>
                <div style={{ fontSize: '16px', fontWeight: '500', lineHeight: '1.6', display: 'inline-block', textAlign: 'left' }}>
                  {lunchSpecials.map((special, index) => (
                    <div key={index} style={{ display: 'grid', gridTemplateColumns: '22px 1fr', alignItems: 'start', gap: '10px', marginBottom: index < lunchSpecials.length - 1 ? '12px' : '0' }}>
                      <svg style={{ width: '1.1em', height: '1.1em', color: 'white', marginTop: '0.2em', display: 'block' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"></path>
                      </svg>
                      <span>{special.name} {special.price}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* White Divider */}
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.3)', margin: '20px 0' }}></div>
              
              {/* Clock Icon */}
              <div style={{ marginBottom: '15px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto'
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'white' }}>
                    <path d="M12 6v6l4 2"></path>
                    <circle cx="12" cy="12" r="10"></circle>
                  </svg>
                </div>
              </div>
              
              {lunchtimeStatus === 'active' && (
                <>
                  <h3 style={{ fontWeight: 'bold', fontSize: '18px', marginBottom: '8px' }}>Lunchtime Special is on!</h3>
                  <p style={{ fontSize: '16px', marginBottom: '12px' }}>Served 11:30 AM - 3 PM daily</p>
                  <div style={{ fontSize: '22px', fontWeight: 'bold' }}>
                    {countdown.hours > 0 && `${countdown.hours}hr `}
                    {countdown.minutes}min {countdown.seconds}s till it ends
                  </div>
                </>
              )}
              
              {lunchtimeStatus === 'waiting' && (
                <>
                  <h3 style={{ fontWeight: 'bold', fontSize: '18px', marginBottom: '8px' }}>Lunchtime Special starts in</h3>
                  <div style={{ fontSize: '22px', fontWeight: 'bold', marginBottom: '8px' }}>
                    {countdown.hours > 0 && `${countdown.hours}hr `}
                    {countdown.minutes}min {countdown.seconds}s
                  </div>
                  <p style={{ fontSize: '14px', opacity: 0.9 }}>Today at 11:30 AM</p>
                </>
              )}
              
              {lunchtimeStatus === 'ended' && (
                <>
                  <p style={{ fontSize: '18px', marginBottom: '8px' }}>Next special starts:</p>
                  <p style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>{nextLunchtime}</p>
                  <div style={{ fontSize: '32px', fontWeight: 'bold' }}>
                    {countdown.hours}hr {countdown.minutes}min {countdown.seconds}s
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Remove the hardcoded steak information since it's now dynamic */}
      </div>
    </div>
  );
};

export default MenuDesign;

